<template>
  <div>
    <!-- 1. Status Bar Wrapper (Static, sits nicely on top with a smooth fade/slide transition on complete) -->
    <transition
      enter-active-class="transition-all duration-300 ease-out overflow-hidden"
      enter-from-class="opacity-0 max-h-0 translate-y-2"
      enter-to-class="opacity-100 max-h-[300px] translate-y-0"
      leave-active-class="transition-all duration-300 ease-in overflow-hidden"
      leave-from-class="opacity-100 max-h-[300px] translate-y-0"
      leave-to-class="opacity-0 max-h-0 translate-y-2"
    >
      <div v-if="uiState === 'initiating' || uiState === 'streaming' || uiState === 'compiling'" class="w-full flex flex-col items-center">
        <status-bar
          :total-step-count="totalStepCount"
          :current-step-number="currentStepNumber"
          :status="status || question.sourcesStatus?.message || 'Initializing answer...'"
          :substatus="substatus"
          :began-at="question.sourcesStatus?.beganAt"
          @cancel="$emit('cancel-loading', 'sources')"
        />
      </div>
    </transition>

    <!-- 2. Action: Load Sources (Static centered display when not loaded and inactive) -->
    <div
      v-if="uiState === 'idle' || uiState === 'failed'"
      class="text-center w-full py-8"
    >
      <!-- Reusable Decoupled Error Card -->
      <research-compass-error-card
        v-if="uiState === 'failed'"
        title="Source Hydration Failed"
        description="An unexpected error occurred while compiling literature sources."
        button-text="Retry Loading"
        button-class="bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
        @retry="getSources()"
      />

      <!-- Standard Load Sources Action -->
      <template v-else-if="uiState === 'idle'">
        <button
          class="bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white px-6 py-2.5 uppercase text-sm font-bold rounded-xl transition-all shadow-lg flex items-center mx-auto gap-2"
          @click="getSources()"
        >
          <i class="fas fa-file-lines"></i>
          Load Sources
        </button>
        <div
          class="mt-4 flex items-center justify-center gap-2 text-gray-500 dark:text-gray-400 text-xs font-semibold italic"
        >
          <i class="fa-regular fa-clock-three text-blue-500/70"></i>
          Usually takes about a minute
        </div>
      </template>
    </div>

    <!-- 3. Unified Content block (displays if loaded OR if streaming draft is available) -->
    <transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0 translate-y-2"
      leave-active-class="transition duration-200 ease-in"
      leave-to-class="opacity-0 translate-y-2"
    >
      <div v-if="uiState === 'streaming' || uiState === 'compiling' || uiState === 'completed'" class="mb-6">
      <!-- Content Area -->
      <div class="min-h-[200px]">
        <!-- 1. Answer (Summary) subtab -->
        <div v-if="selectedSubTab === 'summary'">
          <div v-if="(uiState === 'streaming' || uiState === 'compiling' || uiState === 'completed') && !isClearing">

            <!-- Controls (Smoothly fade in only after final load completes) -->
            <transition
              enter-active-class="transition-all duration-200 ease-out"
              enter-from-class="opacity-0 translate-y-2"
              enter-to-class="opacity-100 translate-y-0"
              leave-active-class="transition-all duration-200 ease-in"
              leave-from-class="opacity-100 translate-y-0"
              leave-to-class="opacity-0 translate-y-2"
            >
              <div v-if="uiState === 'completed'" class="flex flex-wrap gap-2 items-center mb-4">
                <div
                  v-on-click-outside="closeModeDropdown"
                  class="relative inline-block"
                >
                  <button
                    class="inline-flex items-center px-2.5 py-1 rounded-lg bg-blue-100 hover:bg-blue-200 dark:bg-blue-900/30 dark:hover:bg-blue-900/50 text-blue-800 dark:text-blue-300 text-[10px] font-bold uppercase tracking-wider border border-blue-200 dark:border-blue-800/50 shadow-sm transition-colors cursor-pointer"
                    @click="modeDropdownOpen = !modeDropdownOpen"
                  >
                    <i class="fa-solid fa-gauge-high mr-1.5 opacity-70"></i>
                    {{ maxDocCountDisplayString }} Mode
                    <context-popper
                      :text="`Cites up to ${question.maxDocCount} papers`"
                      ><i
                        class="fa-solid fa-circle-info opacity-60 ml-1.5 hover:opacity-100 transition-opacity cursor-help"
                      ></i>
                    </context-popper>
                    <i class="fa-solid fa-caret-down ml-1.5 opacity-60"></i>
                  </button>
                  <div
                    v-if="modeDropdownOpen"
                    class="absolute left-0 top-full mt-1 z-50 bg-white/95 dark:bg-[#0d1a20]/95 backdrop-blur-xl border border-gray-300 dark:border-gray-700 shadow-2xl rounded-lg py-1 min-w-[150px] text-left"
                  >
                    <button
                      class="w-full text-left px-4 py-2 text-xs font-bold text-gray-800 dark:text-gray-200 hover:bg-blue-600 dark:hover:bg-blue-500 hover:text-white transition-colors"
                      :class="{
                        'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400':
                          question.maxDocCount === 10,
                      }"
                      @click="updateQuestionDepth(10)"
                    >
                      Quick (10 papers)
                    </button>
                    <button
                      class="w-full text-left px-4 py-2 text-xs font-bold text-gray-800 dark:text-gray-200 hover:bg-blue-600 dark:hover:bg-blue-500 hover:text-white transition-colors"
                      :class="{
                        'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400':
                          question.maxDocCount === 15,
                      }"
                      @click="updateQuestionDepth(15)"
                    >
                      Balanced (15 papers)
                    </button>
                    <button
                      class="w-full text-left px-4 py-2 text-xs font-bold text-gray-800 dark:text-gray-200 hover:bg-blue-600 dark:hover:bg-blue-500 hover:text-white transition-colors"
                      :class="{
                        'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400':
                          question.maxDocCount === 25,
                      }"
                      @click="updateQuestionDepth(25)"
                    >
                      Deep (25 papers)
                    </button>
                  </div>
                </div>

                <div
                  v-if="modelDisplayString"
                  v-on-click-outside="closeModelDropdown"
                  class="relative inline-block"
                >
                  <button
                    class="inline-flex items-center px-2.5 py-1 rounded-lg bg-purple-100 hover:bg-purple-200 dark:bg-purple-900/30 dark:hover:bg-purple-900/50 text-purple-800 dark:text-purple-300 text-[10px] font-bold uppercase tracking-wider border border-purple-200 dark:border-purple-800/50 shadow-sm transition-colors cursor-pointer"
                    @click="modelDropdownOpen = !modelDropdownOpen"
                  >
                    <i class="fa-solid fa-microchip mr-1.5 opacity-70"></i>
                    {{ modelDisplayString }}
                    <i class="fa-solid fa-caret-down ml-1.5 opacity-60"></i>
                  </button>
                  <div
                    v-if="modelDropdownOpen"
                    class="absolute left-0 top-full mt-1 z-50 bg-white/95 dark:bg-[#0d1a20]/95 backdrop-blur-xl border border-gray-300 dark:border-gray-700 shadow-2xl rounded-lg py-1 min-w-[200px] text-left"
                  >
                    <button
                      v-for="m in models"
                      :key="m.value"
                      class="w-full text-left px-4 py-2 text-xs font-bold transition-all flex items-center justify-between"
                      :class="{
                        'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400':
                          computedQuestionModel === m.value,
                        'opacity-40 cursor-not-allowed text-gray-400 dark:text-gray-500 bg-transparent hover:bg-transparent hover:text-gray-400': m.disabled,
                        'text-gray-800 dark:text-gray-200 hover:bg-blue-600 dark:hover:bg-blue-500 hover:text-white cursor-pointer': !m.disabled
                      }"
                      :disabled="m.disabled"
                      @click="!m.disabled && updateQuestionModel(m.value)"
                    >
                      <span>{{ m.shortLabel }}</span>
                      <span
                        v-if="m.disabled"
                        class="text-[9px] font-extrabold uppercase px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400"
                      >
                        Suspended
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            </transition>

            <!-- Reused Markdown Viewer (Completely stable position and styling) -->
            <!-- Note: '\u258c' is the Unicode character for '▌' (Left Half Block), used as a typing cursor indicator during active streaming -->
            <transition
              mode="out-in"
              enter-active-class="transition-opacity duration-150 ease-out"
              enter-from-class="opacity-0"
              enter-to-class="opacity-100"
              leave-active-class="transition-opacity duration-150 ease-in"
              leave-from-class="opacity-100"
              leave-to-class="opacity-0"
            >
              <research-compass-markdown-viewer
                :key="uiState === 'completed' && shortAnswer ? 'final' : 'streaming'"
                :text="(uiState === 'completed' && shortAnswer) ? shortAnswer : (displayedStreamedAnswer ? displayedStreamedAnswer + (uiState !== 'completed' ? ' \u258c' : '') : '')"
                :sources="question.sources"
                :question="question"
                ref-name="sourceHolder"
                @scroll-to-citation="scrollToCitation"
              />
            </transition>

            <!-- Feedback Buttons (Smoothly fade in only after final load completes) -->
            <transition
              enter-active-class="transition-all duration-200 ease-out"
              enter-from-class="opacity-0 translate-y-2"
              enter-to-class="opacity-100 translate-y-0"
              leave-active-class="transition-all duration-200 ease-in"
              leave-from-class="opacity-100 translate-y-0"
              leave-to-class="opacity-0 translate-y-2"
            >
              <div v-if="uiState === 'completed'" class="flex justify-end mt-2 mb-8">
                <feedback-modal
                  sentiment="POSITIVE"
                  item-type="SHORT_ANSWER"
                  :item-id="question.hashedId"
                  :ancillary-information-label="question.question"
                  :research-compass-hashed-id="$route.params.researchCompassId"
                />
                <feedback-modal
                  sentiment="NEGATIVE"
                  item-type="SHORT_ANSWER"
                  :item-id="question.hashedId"
                  :ancillary-information-label="question.question"
                  :research-compass-hashed-id="$route.params.researchCompassId"
                />
              </div>
            </transition>
          </div>
          <div v-else-if="isClearing" class="py-10 px-6 text-center">
            <status-bar
              :total-step-count="totalStepCount"
              :current-step-number="currentStepNumber"
              :status="status || 'Initializing answer...'"
              :substatus="substatus"
            />
          </div>
        </div>

        <!-- 2. List subtab -->
        <div v-if="selectedSubTab === 'list'" class="w-full">
          <div
            v-for="(source, index) in question.sources"
            :key="source.id"
            :ref="`citation${index + 1}`"
            class="bg-white/60 dark:bg-[#0d1a20]/60 backdrop-blur-xl border border-gray-300 dark:border-gray-700 shadow-xl rounded-2xl p-5 mb-4 transition-all duration-300 hover:shadow-2xl scroll-mt-[150px]"
          >
            <div class="flex items-start w-full">
              <div
                class="bg-blue-600 dark:bg-blue-500 text-white font-mono font-bold w-12 h-12 rounded-xl flex items-center justify-center shrink-0 mr-4 text-xl shadow-md p-0"
              >
                {{ index + 1 }}
              </div>
              <div class="w-full">
                <div class="w-full flex justify-between items-start">
                  <a
                    target="_blank"
                    :href="`${
                      webBaseUrl || ''
                    }/research-compass/paper/${sqidEncode(source.id)}`"
                    class="group"
                  >
                    <h4
                      class="text-lg font-headline font-bold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors flex items-center gap-2"
                    >
                      <span>{{ source.metadata.title }}</span>
                      <i
                        class="fa-solid fa-arrow-up-right-from-square text-[10px] opacity-0 group-hover:opacity-100 transition-opacity"
                      ></i>
                    </h4>
                  </a>
                  <div class="flex items-start gap-1 mt-1">
                    <feedback-modal
                      sentiment="POSITIVE"
                      item-type="PAPER"
                      :item-id="question.hashedId"
                      :ancillary-information="source.id"
                      :ancillary-information-label="source.metadata.title"
                      :research-compass-hashed-id="
                        $route.params.researchCompassId
                      "
                    />
                    <feedback-modal
                      sentiment="NEGATIVE"
                      item-type="PAPER"
                      :item-id="question.hashedId"
                      :ancillary-information="source.id"
                      :ancillary-information-label="source.metadata.title"
                      :research-compass-hashed-id="
                        $route.params.researchCompassId
                      "
                    />
                  </div>
                </div>
                <div class="flex flex-wrap items-center gap-2 my-3 text-[11px]">
                  <span
                    v-if="usedCitationIndices.indexOf(index + 1) !== -1"
                    class="bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-400 border border-emerald-300 dark:border-emerald-800/50 px-2 py-0.5 rounded font-bold uppercase tracking-wider inline-flex items-center shadow-sm"
                  >
                    <i class="fas fa-check-circle mr-1"></i>
                    Cited in Answer
                  </span>
                  <span
                    v-if="source.metadata.year"
                    class="bg-gray-100 dark:bg-gray-800/50 text-gray-800 dark:text-gray-200 border border-gray-200 dark:border-gray-700 px-2 py-0.5 rounded font-mono font-bold flex items-center shadow-sm"
                  >
                    <i class="fas fa-calendar-alt text-gray-500 mr-2"></i>
                    {{ source.metadata.year }}
                  </span>
                  <div
                    class="inline-flex items-center rounded border border-blue-200 dark:border-blue-800/50 overflow-hidden shadow-sm"
                  >
                    <span
                      class="bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 px-2 py-0.5 flex gap-1 items-center font-bold"
                    >
                      <i class="fas fa-book text-blue-500"></i>
                      <span
                        v-if="source.metadata.journal"
                        class="italic max-w-[250px] overflow-hidden whitespace-nowrap overflow-ellipsis"
                      >
                        {{ source.metadata.journal }}
                      </span>
                      <span v-else class="italic">Venue unknown</span>
                    </span>
                    <span
                      v-if="
                        source.metadata.journalQuartile ||
                        source.metadata.journal_quartile ||
                        source.journalQuartile ||
                        source.journal_quartile
                      "
                      class="bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-l border-blue-200 dark:border-blue-800/50 px-2 py-0.5 font-bold uppercase tracking-wider flex items-center"
                    >
                      <context-popper
                        :text="`This journal is ranked as ${
                          source.metadata.journalQuartile ||
                          source.metadata.journal_quartile ||
                          source.journalQuartile ||
                          source.journal_quartile
                        } by SJR`"
                      >
                        <span>{{
                          String(
                            source.metadata.journalQuartile ||
                              source.metadata.journal_quartile ||
                              source.journalQuartile ||
                              source.journal_quartile,
                          ).startsWith("Q")
                            ? source.metadata.journalQuartile ||
                              source.metadata.journal_quartile ||
                              source.journalQuartile ||
                              source.journal_quartile
                            : "Q" +
                              (source.metadata.journalQuartile ||
                                source.metadata.journal_quartile ||
                                source.journalQuartile ||
                                source.journal_quartile)
                        }}</span>
                      </context-popper>
                    </span>
                  </div>
                  <span
                    v-if="source.metadata.citationCount"
                    class="bg-gray-100 dark:bg-gray-800/50 text-gray-800 dark:text-gray-200 border border-gray-200 dark:border-gray-700 px-2 py-0.5 rounded font-bold flex items-center shadow-sm gap-1"
                  >
                    <i class="fas fa-quote-right text-gray-500 text-[10px]"></i>
                    {{ source.metadata.citationCount }} citations
                  </span>
                  <span
                    v-if="source.metadata.influentialCitationCount"
                    class="bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 border border-purple-200 dark:border-purple-800/50 px-2 py-0.5 rounded font-bold flex items-center shadow-sm gap-1"
                  >
                    <span class="font-mono">{{
                      source.metadata.influentialCitationCount
                    }}</span>
                    influential
                  </span>
                  <a
                    v-if="
                      source.metadata.openAccessUrl &&
                      source.metadata.openAccessUrl
                        .toLowerCase()
                        .endsWith('.pdf')
                    "
                    :href="source.metadata.openAccessUrl"
                    target="_blank"
                    class="bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 text-red-800 dark:text-red-300 border border-red-200 dark:border-red-800/50 px-2 py-0.5 rounded font-bold flex items-center shadow-sm transition-colors cursor-pointer"
                  >
                    <i
                      class="fas fa-file-pdf text-red-600 dark:text-red-400 mr-1.5"
                    ></i>
                    PDF
                  </a>
                  <a
                    v-else-if="source.metadata.openAccessUrl"
                    :href="source.metadata.openAccessUrl"
                    target="_blank"
                    class="bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-400 border border-emerald-300 dark:border-emerald-800/50 px-2 py-0.5 rounded font-bold flex items-center shadow-sm transition-colors cursor-pointer"
                  >
                    <i class="fas fa-unlock text-emerald-500 mr-1.5"></i>
                    Open Access
                  </a>
                </div>

                <div
                  v-if="
                    source.metadata.funders &&
                    source.metadata.funders.length > 0
                  "
                  class="mb-3"
                >
                  <div
                    class="text-[10px] font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400 mb-1"
                  >
                    Research Funding
                  </div>
                  <div class="flex flex-wrap gap-1.5">
                    <span
                      v-for="(funder, idx) in source.metadata.funders"
                      :key="idx"
                      class="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300 rounded border border-emerald-300 dark:border-emerald-800/50"
                    >
                      <i
                        class="fas fa-building-columns text-emerald-500 mr-0.5"
                      ></i>
                      {{
                        funder.funderDisplayName || funder.funder_display_name
                      }}
                    </span>
                  </div>
                </div>

                <div v-else class="mb-3">
                  <div
                    class="text-[10px] font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400 mb-1"
                  >
                    Research Funding
                  </div>
                  <div
                    class="text-[11px] text-gray-400 dark:text-gray-500 italic"
                  >
                    Funding data is currently unavailable for this publication.
                  </div>
                </div>

                <div class="flex flex-wrap my-3 gap-y-2">
                  <div
                    v-for="authorId in source.metadata.authorIds"
                    :key="authorId"
                    class="mr-4"
                  >
                    <a
                      v-if="question.authors && question.authors[authorId]"
                      :href="`${
                        webBaseUrl || ''
                      }/research-compass/author/${sqidEncode(authorId)}`"
                      class="text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 text-sm flex items-center transition-colors"
                      target="_blank"
                    >
                      <i
                        class="fa-solid fa-user text-[10px] mr-1.5 opacity-60"
                      ></i>
                      <span class="font-semibold">{{
                        question.authors[authorId].name
                      }}</span>
                    </a>
                  </div>
                </div>

                <div
                  v-if="source.metadata.contribution"
                  class="bg-blue-500/5 dark:bg-blue-400/5 border-l-4 border-blue-500/50 dark:border-blue-400/30 rounded-r-lg px-5 py-4 mt-4 text-sm text-gray-700 dark:text-gray-300 italic shadow-sm"
                >
                  <div
                    class="text-[10px] font-bold uppercase tracking-widest text-blue-600 dark:text-blue-400 mb-1 not-italic"
                  >
                    Key Contribution
                  </div>
                  {{ source.metadata.contribution }}
                </div>
                <div v-else class="px-3 mb-4">
                  <div
                    class="rounded-xl bg-emerald-500/10 dark:bg-emerald-400/5 px-4 py-3 border border-emerald-500/20 dark:border-emerald-400/10 shadow-sm"
                  >
                    <div
                      class="text-[10px] font-bold uppercase tracking-widest text-emerald-700 dark:text-emerald-400 mb-1 flex items-center"
                    >
                      <i class="fas fa-sparkles mr-2 opacity-70"></i>
                      Why it fits
                    </div>
                    <div class="flex items-center">
                      <i
                        class="text-sm fas fa-circle-notch fa-spin text-emerald-400 mr-2"
                      ></i>
                      <p class="italic text-xs text-gray-500">
                        Generating contribution summary...
                      </p>
                    </div>
                  </div>
                </div>

                <div
                  class="text-sm text-gray-600 dark:text-gray-400 leading-relaxed line-clamp-3 mt-4"
                >
                  {{ source.page_content }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 3. Table subtab -->
        <div v-if="selectedSubTab === 'table'" class="w-full">
          <div v-if="generatingTable" class="py-10 px-6">
            <status-bar
              :total-step-count="totalStepCount"
              :current-step-number="currentStepNumber"
              :status="status || 'Generating table...'"
              :substatus="substatus"
            />
          </div>
          <div
            v-else-if="!table"
            class="py-10 text-center bg-gray-50/50 dark:bg-gray-900/30 rounded-2xl border border-dashed border-gray-300 dark:border-gray-700"
          >
            <button
              class="bg-white dark:bg-gray-800 hover:bg-blue-50 dark:hover:bg-blue-900/30 border border-blue-200 dark:border-blue-700 text-blue-700 dark:text-blue-400 px-6 py-2.5 uppercase text-xs font-bold rounded-xl transition-all shadow-sm flex items-center mx-auto gap-2"
              @click="generateTable()"
            >
              <i class="fas fa-wand-magic-sparkles"></i>
              Generate Tabular Detail
            </button>
            <div
              class="mt-4 flex items-center justify-center gap-2 text-gray-500 dark:text-gray-400 text-xs font-semibold italic"
            >
              <i class="fa-regular fa-clock-three text-blue-500/70"></i>
              Usually takes about a minute
            </div>
          </div>
          <div v-else class="overflow-x-auto pb-4 custom-scrollbar">
            <research-compass-markdown-viewer
              :text="table"
              :sources="question.sources"
              :question="question"
              ref-name="sourceHolder"
              :show-table-scroll-indicators="true"
              @scroll-to-citation="scrollToCitation"
            />
            <div class="flex justify-end mt-4">
              <feedback-modal
                sentiment="POSITIVE"
                item-type="TABLE"
                :item-id="question.hashedId"
                :ancillary-information-label="question.question"
                :research-compass-hashed-id="$route.params.researchCompassId"
              />
              <feedback-modal
                sentiment="NEGATIVE"
                item-type="TABLE"
                :item-id="question.hashedId"
                :ancillary-information-label="question.question"
                :research-compass-hashed-id="$route.params.researchCompassId"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    </transition>
  </div>
</template>

<script>
import { nextTick } from "vue";
import { vOnClickOutside } from "@vueuse/components";
import * as utils from "../../utils";
import StatusBar from "./StatusBar.vue";
import FeedbackModal from "./FeedbackModal.vue";
import ContextPopper from "../ContextPopper.vue";
import ResearchCompassMarkdownViewer from "./ResearchCompassMarkdownViewer.vue";
import ResearchCompassErrorCard from "./ResearchCompassErrorCard.vue";

const { sqidEncode, resolveRcApiUrl } = utils;

const QuestionState = {
  IDLE: "idle",
  INITIATING: "initiating",
  STREAMING: "streaming",
  COMPILING: "compiling",
  COMPLETED: "completed",
  FAILED: "failed"
};

const ALLOWED_TRANSITIONS = {
  [QuestionState.IDLE]:       [QuestionState.INITIATING, QuestionState.FAILED],
  [QuestionState.INITIATING]: [QuestionState.STREAMING, QuestionState.FAILED],
  [QuestionState.STREAMING]:  [QuestionState.COMPILING, QuestionState.FAILED],
  [QuestionState.COMPILING]:  [QuestionState.COMPLETED, QuestionState.FAILED],
  [QuestionState.COMPLETED]:  [QuestionState.INITIATING, QuestionState.FAILED],
  [QuestionState.FAILED]:     [QuestionState.INITIATING, QuestionState.FAILED]
};

export default {
  directives: {
    onClickOutside: vOnClickOutside,
  },
  components: {
    StatusBar,
    FeedbackModal,
    ContextPopper,
    ResearchCompassMarkdownViewer,
    ResearchCompassErrorCard,
  },
  props: {
    question: Object,
    shortAnswer: String,
    loadedSources: Boolean,
    loadingSources: Boolean,
    selectedSubTab: String,
    isClearing: Boolean,
    isInitiating: Boolean,
    models: Array,
    maxDocCountDisplayString: String,
    modelDisplayString: String,
    computedQuestionModel: String,
  },
  emits: [
    "reload-research-compass",
    "start-loading",
    "finish-loading",
    "cancel-loading",
    "change-sub-tab",
    "update-question-depth",
    "update-question-model",
    "get-sources",
    "update:generating-table",
    "stream-start",
  ],
  data() {
    return {
      uiState: QuestionState.IDLE,
      totalStepCount: 0,
      currentStepNumber: 0,
      status: "",
      substatus: "",
      modeDropdownOpen: false,
      modelDropdownOpen: false,
      generatingTable: false,
      researchCompassQuestionChannel: null,
      displayedStreamedAnswer: "",
    };
  },
  computed: {
    isFailed() {
      const status = this.question.sourcesStatus;
      return !!(status?.hasError || status?.message === "Error");
    },
    webBaseUrl() {
      return import.meta.env.VITE_WEB_BASE_URL;
    },
    usedCitationIndices() {
      const regex = /\[([0-9, ]*?)\]/g;
      let result = this.shortAnswer || "";
      const matches = result.match(regex);
      if (!matches) return [];
      [...new Set(matches)].forEach((match) => {
        if (match.indexOf(",") > -1) {
          const parts = match
            .slice(1, -1)
            .split(",")
            .map((part) => part.trim());
          const formattedParts = parts.map((part) => `[${part}]`);
          result = result.replaceAll(match, formattedParts.join(" "));
        }
      });
      const newMatches = result.match(regex);
      return [...new Set(newMatches)].map((item) =>
        Number(item.replace("[", "").replace("]", "").replace(" ", "")),
      );
    },
    table() {
      const answers = this.question.answers || [];
      const tables = answers
        .filter(
          (answer) =>
            (answer.answerType?.value || answer.answerType) === "TABLE",
        )
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      if (tables.length > 0) {
        return tables[0].content;
      }
      return null;
    },
    isPublic() {
      return Boolean(this.$route.params.viewKey);
    },
    isCollaborationSpace() {
      return Boolean(this.$route.params.collaborationSpaceId);
    },
    activeAxios() {
      if (this.isPublic) {
        return this.$api.axiosPublic;
      }
      return this.$api.axios;
    },
  },
  watch: {
    generatingTable(newVal) {
      this.$emit("update:generating-table", newVal);
    },
    table(newVal) {
      if (newVal) {
        this.generatingTable = false;
      }
    },
    loadingSources(newVal) {
      if (newVal && this.uiState === QuestionState.IDLE) {
        this.transitionTo(QuestionState.INITIATING);
      }
    },
    isInitiating(newVal) {
      if (newVal) {
        this.transitionTo(QuestionState.INITIATING);
        this.resetProgressState();
      }
    },
    question(newVal, oldVal) {
      if (newVal?.hashedId !== oldVal?.hashedId) {
        this.resetStream();
      }
    },
  },
  mounted() {
    this.setUpChannel(this.question.hashedId);
    if (this.loadedSources) {
      this.uiState = QuestionState.COMPLETED;
    } else if (this.isFailed) {
      this.uiState = QuestionState.FAILED;
    } else if (this.loadingSources || this.isInitiating) {
      this.uiState = QuestionState.INITIATING;
      this.displayedStreamedAnswer = "";
    }
  },
  unmounted() {
    if (this.researchCompassQuestionChannel) {
      this.researchCompassQuestionChannel.unsubscribe();
      this.researchCompassQuestionChannel = null;
    }
  },
  methods: {
    transitionTo(nextState) {
      const currentState = this.uiState;
      if (!ALLOWED_TRANSITIONS[currentState].includes(nextState)) {
        console.warn(`[FSM] Ignored illegal transition: ${currentState} -> ${nextState}`);
        return;
      }

      switch (nextState) {
        case QuestionState.INITIATING:
          this.displayedStreamedAnswer = ""; // Purge old tokens on retry
          this.$emit("start-loading", "sources");
          break;
        case QuestionState.COMPLETED:
          this.$emit("finish-loading", "sources");
          break;
        case QuestionState.FAILED:
          this.generatingTable = false;
          this.$emit("cancel-loading", "sources");
          break;
      }
      this.uiState = nextState;
    },
    sqidEncode(id) {
      return sqidEncode(id);
    },
    scrollToCitation(citationId) {
      this.$emit("change-sub-tab", "list");
      nextTick(() => {
        const ref = this.$refs[`citation${citationId}`];
        const el = Array.isArray(ref) ? ref[0] : ref;
        const targetEl = el?.$el || el;
        if (targetEl) {
          targetEl.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
    },
    setUpChannel(hashedId) {
      if (this.researchCompassQuestionChannel === null) {
        const channelName = `${import.meta.env.VITE_ENV_CONFIG_NAMESPACE || ""}researchcompassquestion${hashedId}sources`;
        const channel = this.$ably.channels.get(channelName);

        const self = this;
        channel.subscribe((message) => {
          let data;
          try {
            data =
              typeof message.data === "string"
                ? JSON.parse(message.data)
                : message.data;
          } catch (e) {
            console.error("Failed to parse websocket message", e);
            return;
          }

          if (!data) return;

          const statusData = data.metadata || data;

          if (statusData.status !== "Error") {
            if (self.uiState === QuestionState.IDLE) {
              self.transitionTo(QuestionState.INITIATING);
            }
            if (statusData.status) self.status = statusData.status;
            if (statusData.substatus !== undefined)
              self.substatus = statusData.substatus;
            if (statusData.totalStepCount)
              self.totalStepCount = statusData.totalStepCount;
            if (statusData.currentStepNumber)
              self.currentStepNumber = statusData.currentStepNumber;
            if (statusData.chunk) {
              if (self.uiState === QuestionState.IDLE || self.uiState === QuestionState.INITIATING) {
                self.transitionTo(QuestionState.STREAMING);
              }
              self.handleStreamChunk(statusData.chunk);
            }
            if (statusData.status && statusData.status !== "Error" && self.uiState === QuestionState.STREAMING) {
              self.transitionTo(QuestionState.COMPILING);
            }
            if (statusData.reload) {
              self.$emit("reload-research-compass");
            }
            if (statusData.finished) {
              self.transitionTo(QuestionState.COMPLETED);
            }
          } else {
            if (statusData.reload) {
              self.$emit("reload-research-compass");
            }
            self.transitionTo(QuestionState.FAILED);
          }
        });
        this.researchCompassQuestionChannel = channel;
      }
    },
    getSources() {
      this.transitionTo(QuestionState.INITIATING);
      this.status = "Initializing source hydration...";
      this.substatus = "";
      this.totalStepCount = 10;
      this.currentStepNumber = 1;
      this.$emit("get-sources");
    },
    handleStreamChunk(chunk) {
      if (!chunk) return;
      const isFirstChunk = !this.displayedStreamedAnswer;
      this.displayedStreamedAnswer += chunk;
      if (isFirstChunk) {
        this.$emit("stream-start");
      }
    },
    resetStream() {
      this.displayedStreamedAnswer = "";
    },
    generateTable() {
      this.generatingTable = true;
      this.status = "Initializing comparison...";
      this.substatus = "";
      this.totalStepCount = 10;
      this.currentStepNumber = 1;
      const url = resolveRcApiUrl(
        this.question.hashedId,
        "generate-table",
        this.$route.params,
      );

      this.activeAxios.post(url).catch(() => {
        this.generatingTable = false;
      });
    },
    resetProgressState() {
      this.resetStream();
      this.status = "Initializing answer...";
      this.substatus = "";
      this.totalStepCount = 10;
      this.currentStepNumber = 1;
    },
    updateQuestionDepth(depth) {
      this.modeDropdownOpen = false;
      this.resetProgressState();
      this.$emit("update-question-depth", depth);
    },
    updateQuestionModel(model) {
      this.modelDropdownOpen = false;
      this.resetProgressState();
      this.$emit("update-question-model", model);
    },
    closeModeDropdown() {
      this.modeDropdownOpen = false;
    },
    closeModelDropdown() {
      this.modelDropdownOpen = false;
    },
  },
};
</script>
